import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useId,
} from "react";
import { createPortal } from "react-dom";
import styles from "./Select.module.css";
import { ISelectOption } from "../../../types/ISelectOption";
import { useFieldId, useFieldLabelId } from "../FieldWrapper/FieldWrapper";
import clsx from "clsx";

type SelectValue = string | number | null;
type SelectSize = "md" | "sm";

const DROPDOWN_GAP = 4;
/** Mirrors `.dropdown`'s max-height; the viewport can only shrink it, never grow it. */
const DROPDOWN_MAX_HEIGHT = 240;
/** Below this the list is barely scrollable — flip above the trigger if that side has more room. */
const MIN_SPACE_BELOW = 160;

interface DropdownPlacement {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

/**
 * Viewport coordinates for the portaled list. The dropdown used to be absolutely positioned
 * inside the field, which any `overflow` ancestor — a modal's scrollable body above all — clipped
 * to invisibility. Fixed positioning against the trigger's rect escapes every such container;
 * flipping above when the trigger sits near the viewport bottom keeps the list readable there.
 */
function placeDropdown(trigger: HTMLElement): DropdownPlacement {
  const rect = trigger.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP;
  const spaceAbove = rect.top - DROPDOWN_GAP;
  const openUp = spaceBelow < MIN_SPACE_BELOW && spaceAbove > spaceBelow;

  if (openUp) {
    return {
      left: rect.left,
      width: rect.width,
      bottom: window.innerHeight - rect.top + DROPDOWN_GAP,
      maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, spaceAbove),
    };
  }
  return {
    left: rect.left,
    width: rect.width,
    top: rect.bottom + DROPDOWN_GAP,
    // Both sides can be short; 120px keeps the list usable rather than a sliver.
    maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, Math.max(spaceBelow, 120)),
  };
}

interface SelectProps {
  options: ISelectOption[];

  value?: SelectValue;
  defaultValue?: SelectValue;
  onChange?: (value: SelectValue) => void;

  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: string | null;

  size?: SelectSize;

  onOpenChange?: (open: boolean) => void;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;

  /**
   * Put on the focusable `role="combobox"` node so an enclosing `FieldWrapper`'s `<label htmlFor>`
   * resolves to it. Normally supplied by `FormSelect` from the field-id context.
   */
  id?: string;

  /** Announced as `aria-required` — a `role="combobox"` div gets no such semantics for free. */
  required?: boolean;

  /**
   * Accessible name for a `Select` used outside a `FieldWrapper` — a compact toolbar control that
   * has no visible label of its own. Ignored when a wrapper is present: the visible label wins.
   */
  ariaLabel?: string;
}

const Select: FC<SelectProps> = ({
  options,
  value,
  defaultValue = null,
  onChange,
  placeholder = "Select...",
  className,
  disabled = false,
  error,
  size = "md",
  onOpenChange,
  onFocus,
  id: idProp,
  required,
  ariaLabel,
}) => {
  const generatedId = useId();
  /**
   * Read straight from the enclosing `FieldWrapper` when no `id` is passed, so a `Select` used
   * directly — the filter bars and the entity wrappers like `WorkOrderStatusSelect` — is labelled
   * too, not only the ones that go through `FormSelect`.
   */
  const fieldId = useFieldId();
  // `htmlFor` cannot reach a `<div role="combobox">`; this is how the label gets attached.
  const labelId = useFieldLabelId();
  const id = idProp ?? fieldId ?? generatedId;
  const listboxId = `${id}-listbox`;
  const errorId = `${id}-error`;

  const isControlled = value !== undefined;

  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<SelectValue>(defaultValue);
  /**
   * The option the arrow keys are on, which is not the same thing as the selected one — moving
   * through the list must not commit anything until Enter. `-1` while nothing is highlighted.
   */
  const [activeIndex, setActiveIndex] = useState(-1);

  const selectRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeOptionRef = useRef<HTMLDivElement | null>(null);

  const [placement, setPlacement] = useState<DropdownPlacement | null>(null);

  const currentValue = isControlled ? value : internalValue;

  const selectedLabel = useMemo(() => {
    if (currentValue === null || currentValue === undefined) return "";
    return options.find((option) => option.value === currentValue)?.label ?? "";
  }, [currentValue, options]);

  const close = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const selectedIndex = useMemo(
    () => options.findIndex((option) => option.value === currentValue),
    [options, currentValue],
  );

  /** Opening always highlights something, so the first arrow press moves rather than initialises. */
  const openList = useCallback(() => {
    if (disabled) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
    onOpenChange?.(true);
  }, [disabled, selectedIndex, onOpenChange]);

  const toggle = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;

    if (open) close();
    else openList();
  };

  const commit = useCallback(
    (nextValue: SelectValue) => {
      if (disabled) return;

      if (!isControlled) {
        setInternalValue(nextValue);
      }

      onChange?.(nextValue);
      close();
    },
    [disabled, isControlled, onChange, close],
  );

  const handleSelect = (nextValue: SelectValue, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    commit(nextValue);
  };

  /**
   * The keyboard contract of a listbox combobox, which this control had none of: it was
   * `tabIndex={0}` and therefore a tab stop, but no key did anything once you got there. Status and
   * Wiederholung were mouse-only.
   *
   * Every branch calls `preventDefault`, for two different reasons. Space and the arrows would
   * scroll the dialog behind the list. Enter would reach the `<form>` and **submit it** — the form
   * now submits on Enter (see `Modal`'s `SubmitButtonConfig.formId`), so a combobox that lets Enter
   * through would save the Termin instead of picking a status.
   *
   * Escape stops propagating only when it actually closed something. `modalStack` listens for it on
   * the way up precisely so this can happen first: one press shuts the list, the next closes the
   * dialog.
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        if (!open) {
          openList();
          return;
        }
        const step = event.key === "ArrowDown" ? 1 : -1;
        setActiveIndex((prev) => {
          const next = prev + step;
          if (next < 0) return options.length - 1;
          if (next >= options.length) return 0;
          return next;
        });
        return;
      }
      case "Home":
      case "End": {
        if (!open) return;
        event.preventDefault();
        setActiveIndex(event.key === "Home" ? 0 : options.length - 1);
        return;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        if (!open) {
          openList();
          return;
        }
        const option = options[activeIndex];
        if (option) commit(option.value as SelectValue);
        return;
      }
      case "Escape": {
        if (!open) return;
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      case "Tab": {
        // Leaving the control abandons the highlight rather than committing it.
        if (open) close();
      }
    }
  };

  useEffect(() => {
    if (open) activeOptionRef.current?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  /**
   * Position before paint, then follow the trigger. The capture-phase scroll listener is what
   * catches a scrolling modal body — scroll events do not bubble, so a plain listener on `window`
   * would leave the list hanging where the field used to be.
   */
  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }

    const update = () => {
      if (triggerRef.current) setPlacement(placeDropdown(triggerRef.current));
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  /**
   * Outside-click with the portaled list counted as inside. `useOutsideClick(selectRef, …)` would
   * close on the mousedown of an option click — the list lives on `document.body` now — and the
   * unmount would swallow the click before it could commit a value.
   */
  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (selectRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      close();
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open, close]);

  const hasValue = currentValue !== null && currentValue !== undefined && currentValue !== "";

  return (
    <div
      ref={selectRef}
      className={clsx(
        styles.container,
        size === "sm" && styles.sizeSm,
        className,
      )}
      onClick={(event) => event.stopPropagation()}
    >
      {/* `tabIndex` and `onFocus` moved here from the wrapper. They used to sit on different nodes
          from `role="combobox"` and the `aria-*`, so the element the user focused and the element
          the screen reader described were not the same one. */}
      <div
        ref={triggerRef}
        // On the focusable node, so the `FieldWrapper` label resolves here and not to the wrapper.
        id={id}
        className={clsx(
          styles.trigger,
          disabled && styles.triggerDisabled,
          error && styles.triggerError,
        )}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-labelledby={labelId}
        aria-label={labelId ? undefined : ariaLabel}
        aria-disabled={disabled}
        aria-required={required || undefined}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        aria-activedescendant={
          open && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined
        }
      >
        <span className={clsx(styles.value, !hasValue && styles.placeholder)}>
          {hasValue ? selectedLabel : placeholder}
        </span>

        <svg
          className={clsx(styles.chevron, open && styles.chevronOpen)}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M5 8L10 13L15 8"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {open && !disabled && placement && createPortal(
        <div
          id={listboxId}
          ref={dropdownRef}
          className={styles.dropdown}
          role="listbox"
          // Overrides the class's in-flow absolute positioning; see `placeDropdown`. z-index above
          // the modal container's 100 — the list must paint over the dialog that anchors it.
          style={{
            position: "fixed",
            left: placement.left,
            width: placement.width,
            top: placement.top,
            bottom: placement.bottom,
            maxHeight: placement.maxHeight,
            marginTop: 0,
            zIndex: 1000,
          }}
        >
          {options.map((option, index) => {
            const isSelected = option.value === currentValue;
            const isActive = index === activeIndex;
            /**
             * Headings are emitted as siblings inside this same pass rather than by grouping the
             * options first. That is the whole point: `activeIndex`, `aria-activedescendant` and
             * the arrow keys all index `options`, so a nested structure would have to renumber
             * them. A flat list means groups cost the keyboard nothing.
             */
            const startsGroup =
              Boolean(option.group) && option.group !== options[index - 1]?.group;

            return (
              <React.Fragment key={String(option.value)}>
                {startsGroup && (
                  <div className={styles.groupHeader} role="presentation">
                    {option.group}
                  </div>
                )}
                <div
                  id={`${id}-option-${index}`}
                  // The *active* option is the one scrolled to, not the selected one: arrowing down a
                  // long list has to keep the highlight in view.
                  ref={isActive ? activeOptionRef : null}
                  className={clsx(
                    styles.option,
                    option.group && styles.optionGrouped,
                    isSelected && styles.optionSelected,
                    isActive && styles.optionActive,
                  )}
                  onClick={(event) => handleSelect(option.value as SelectValue, event)}
                  // Pointer and keyboard share one highlight, so hovering does not leave a second
                  // one behind for Enter to commit.
                  onMouseEnter={() => setActiveIndex(index)}
                  role="option"
                  aria-selected={isSelected}
                  // The heading is `presentation`, so the group name would otherwise be visual
                  // only. Carrying it in the option's name is what a flat listbox can offer.
                  aria-label={option.group ? `${option.group}: ${option.label}` : undefined}
                >
                  {option.label}
                </div>
              </React.Fragment>
            );
          })}
        </div>,
        document.body,
      )}

      {error && (
        <span id={errorId} className={styles.errorText} role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default Select;
